import { Link } from 'react-router-dom'
import resolve from '../../../../common/utils/object/resolve.js'
import { evaluateCondition } from '../../../../common/model/condition/evaluate.js'
import useLocale from '../../hooks/useLocale'
import usePath from '../../hooks/usePath'
import { TableDecorator } from './Table'
import LineActions from '../listView/LineActions'
import SelectionCheckbox from '../listView/SelectionCheckbox'
import { 
    TableList, 
    TableListItem, 
    ActionsOverlay, 
    TableCell, 
    NoData,
    createSlugPath,
    hasValidData
} from './Common'

//Importing subcomponent for ensuring the correct display of the data
import DisplayElement from '../display/DisplayElement'

const TableRows = ({
    endpoint,
    result,
    columns,
    slug = '',
    tableKey = 'id',
    path = '',
    pathTemplate,
    lineActions,
    onAction,
    listActions,
    selectedItems,
    onItemSelect,
    clickLine,
    onExpand,
    selectable,
    showOnMobile,
    draggable,
    // { condition } — the rows it holds for are set in bold. A chart of
    // accounts uses it to separate its headings and sums from the accounts
    // underneath them; any list that mixes structure with data can.
    emphasizeLine,
}) => {
    const { lf } = useLocale()
    // format the path by merging the endpoint to the current url
    const { url } = usePath(path)

    const hasListActions = listActions && listActions.menuItems && listActions.menuItems.length > 0
    const showCheckbox = hasListActions || selectable

    const slugPath = createSlugPath(slug)

    const isEmphasized = (item) =>
        emphasizeLine ? evaluateCondition(emphasizeLine.condition, { values: item }) : false

    const resolveTemplate = (template, item) =>
        template.replace(/\{(\w+)\}/g, (_, key) => item[key] ?? '')

    const getItemUrl = (item) => {
        if (Array.isArray(pathTemplate)) {
            for (const pt of pathTemplate) {
                if (!pt.condition) return resolveTemplate(pt.template, item)
                if (item[pt.condition.field] === pt.condition.value) return resolveTemplate(pt.template, item)
            }
        }
        if (pathTemplate) return resolveTemplate(pathTemplate, item)
        return `${url}${item[tableKey]}${slugPath}`
    }

    if (!hasValidData(result)) {
        // Handle the case when result data is empty
        return NoData;
    }

    return (
        <TableList $showOnMobile={showOnMobile}>
            {result.data.pages.map((page, pageIndex) => (
                <div key={pageIndex}>
                    {
                        Array.isArray(page.data) && page.data.map(
                            (item, i) => {
                                const isSelected = selectedItems && selectedItems.has(item.id)
                                return (
                                    <TableListItem
                                        data-testid={`${endpoint}.list.${i+1}`}
                                        key={i}
                                        to={getItemUrl(item)}
                                        $isSelected={isSelected}
                                        $emphasis={isEmphasized(item)}
                                        as = {clickLine === 'expand' ? 'div' : Link}
                                        onClick={clickLine === 'expand' && onExpand ? () => onExpand(item) : undefined}
                                        draggable={!!draggable}
                                        onDragStart={draggable ? (e) => {
                                            e.dataTransfer.effectAllowed = 'move'
                                            e.dataTransfer.setData('text/plain', JSON.stringify({ id: item.id, folderId: item.folderId }))
                                        } : undefined}
                                    >
                                        {/* Selection checkbox column */}
                                        {showCheckbox && (
                                            <TableCell $len="18px" $svgMargin="0">
                                                <SelectionCheckbox
                                                    state={isSelected ? 'selected' : 'none'}
                                                    onChange={(opts) => onItemSelect && onItemSelect(item.id, opts)}
                                                />
                                            </TableCell>
                                        )}
                                        
                                        {/* Regular columns */}
                                        {
                                            columns.map(
                                                (col, j) => {
                                                    return (
                                                        <TableCell key={j} $len={col.width} $shrink={col.shrink}>
                                                            <p data-testid={`${endpoint}.list.${i+1}.${col.id}`}><DisplayElement string={resolve(col.id, item) ?? (col.fallback && resolve(col.fallback, item))} type={col.type} col={col} item={item} /></p>
                                                        </TableCell>
                                                    )
                                                }
                                            )
                                        }
                                        {lineActions && (
                                            <ActionsOverlay>
                                                <LineActions 
                                                    lineActions={lineActions} 
                                                    item={item} 
                                                    onAction={onAction}
                                                />
                                            </ActionsOverlay>
                                        )}
                                    </TableListItem>
                                )
                            }
                        )
                    }
                </div>)
            )}


        </TableList>
    )
}

export default TableRows
